/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();

      // If Shopify's 250-variant Liquid cap truncated our data, fetch the rest via Storefront API

      const totalVariantCount = parseInt(this.dataset.variantCount || 250, 10);
      if (totalVariantCount >= this.data.product.variants.length) {
        this.fetchAllVariants(totalVariantCount);
      }
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
        this.updatePrice();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (!selector) return; 

      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      const valueEl = evt.target.nextElementSibling?.querySelector('.js-value');
      if (!valueEl) return;

      colorText.textContent = valueEl.textContent;
    }


    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        if (typeof this.pickUpAvailability.getAvailability === 'function') {
          this.pickUpAvailability.getAvailability(this.variant.id);
        }
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
     
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {

        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }


      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // cpy_changes.
        const currentLabelEl = priceCurrentEl?.querySelector('.js-label');
        const currentValueEl = priceCurrentEl?.querySelector('.js-value');
        const wasLabelEl = priceWasEl?.querySelector('.js-label');
        const wasValueEl = priceWasEl?.querySelector('.js-value');


        // Update price
        if (variant.compare_at_price > variant.price) {

          if (currentLabelEl) currentLabelEl.textContent = theme.strings.salePrice;
          if (currentValueEl) currentValueEl.innerHTML = this.data.formatted[variant.id].price;

          if (priceWasEl) {
            if (wasLabelEl) wasLabelEl.textContent = theme.strings.regularPrice;
            if (wasValueEl) wasValueEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
          }
        } else {

          if (currentLabelEl) currentLabelEl.textContent = theme.strings.regularPrice;
          if (currentValueEl) currentValueEl.innerHTML = this.data.formatted[variant.id].price;

          if (priceWasEl) {
            priceWasEl.hidden = true; // ← add this
            if (wasLabelEl) wasLabelEl.textContent = '';
            if (wasValueEl) wasValueEl.innerHTML = '';
          }
        }


        // Update unit price, if specified.
        // if (this.variant.unit_price_measurement) {
        //   const valueEl = this.price.querySelector('.unit-price__price');
        //   const unitEl = this.price.querySelector('.unit-price__unit');
        //   const value = this.variant.unit_price_measurement.reference_value;
        //   const unit = this.variant.unit_price_measurement.reference_unit;

        //   valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
        //   unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        // }

        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');

          if (valueEl && unitEl) {
            const value = this.variant.unit_price_measurement.reference_value;
            const unit = this.variant.unit_price_measurement.reference_unit;

            valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
            unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          }
        }

        

        // unitPriceEl.hidden = !this.variant.unit_price_measurement;
        unitPriceEl.hidden = !this.variant.unit_price_measurement;

        // if (unitPriceEl) {
        //   const showUnitPrice = !!this.variant.unit_price_measurement;
        //   unitPriceEl.hidden = !showUnitPrice;
        //   unitPriceEl.style.display = showUnitPrice ? '' : 'none'; // ← hides ::before and ::after too
        // }

        if (unitPriceEl) {
          const hasUnitPrice = !!(
            this.variant.unit_price_measurement &&
            this.data.formatted[this.variant.id]?.unitPrice
          );
          unitPriceEl.hidden = !hasUnitPrice;
          unitPriceEl.style.display = hasUnitPrice ? '' : 'none';
        }

        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Fetches ALL variants via the Shopify Storefront API when Liquid's 250-variant
     * cap has truncated the embedded JSON. Merges the fetched variants into
     * this.data.product.variants and re-runs availability + button updates.
     *
     * Requires either:
     *   a) A `storefront_api_token` theme setting, mapped to data-storefront-token, OR
     *   b) window.storefrontToken set globally in theme.liquid
     *
     * @param {number} totalCount - True total variant count from data-variant-count.
     */
    async fetchAllVariants(totalCount) {
      const token =
        this.dataset.storefrontToken ||
        window.storefrontToken ||
        (window.Shopify && window.Shopify.storefrontToken);

      if (!token) {
        console.warn(
          '[variant-picker] Product has ' + totalCount + ' variants but only 250 were ' +
          'loaded from Liquid. To load all variants, provide a Storefront API public ' +
          'access token via: (1) a theme setting named "storefront_api_token", ' +
          '(2) window.storefrontToken, or (3) window.Shopify.storefrontToken.'
        );
        return;
      }

      const imageToMediaMap = {};
      this.data.product.variants.forEach(v => {
        if (v.featured_image && v.featured_media) {
          imageToMediaMap[v.featured_image.id] = v.featured_media.id;
        }
      });

      const productId = this.dataset.productId;
      const shop = (window.Shopify && window.Shopify.shop) || window.location.hostname;


      const endpoint = `https://${shop}/api/2024-01/graphql.json`;
      const gid = `gid://shopify/Product/${productId}`;

      // We already have the first batch from Liquid. Start cursor-based pagination
      // from the beginning so we can collect ALL variants reliably.
      const allVariants = [];
      let cursor = null;
      let hasNextPage = true;

      try {
        while (hasNextPage) {
          const afterClause = cursor ? `, after: "${cursor}"` : '';
          const query = `{
            product(id: "${gid}") {
              variants(first: 250${afterClause}) {
                pageInfo { hasNextPage endCursor }
                nodes {
                  id
                  title
                  availableForSale
                  sku
                  barcode
                  weight
                  weightUnit
                  price { amount }
                  compareAtPrice { amount }
                  unitPrice { amount }
                  selectedOptions { name value }
                  image {
                    id
                    url
                  }
                  unitPriceMeasurement {
                    referenceValue
                    referenceUnit
                  }
                }
              }
            }
          }`;

          // eslint-disable-next-line no-await-in-loop
          const resp = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token
            },
            body: JSON.stringify({ query })
          });


          if (!resp.ok) {
            console.error('[variant-picker] Storefront API error:', resp.status);
            return;
          }

          // eslint-disable-next-line no-await-in-loop
          const json = await resp.json();

          const variantData = json?.data?.product?.variants;

          if (!variantData) {
            console.error('[variant-picker] Unexpected Storefront API response', json);
            return;
          }

          variantData.nodes.forEach((node) => {
            // Convert Storefront API shape → legacy REST shape expected by the rest of the JS
            const legacyId = parseInt(node.id.replace(/[^0-9]/g, ''), 10);
            const price = Math.round(parseFloat(node.price.amount) * 100);
            const compareAtPrice = node.compareAtPrice
              ? Math.round(parseFloat(node.compareAtPrice.amount) * 100)
              : null;
            allVariants.push({
              id: legacyId,
              title: node.title,
              available: node.availableForSale,
              options: node.selectedOptions.map((o) => o.value),
              price: price,
              compare_at_price: compareAtPrice,
              sku: node.sku,
              barcode: node.barcode,
              weight: node.weight,
              weight_unit: node.weightUnit,
              inventory_management: node.inventoryManagement ? 'shopify' : null,
              inventory_quantity: node.inventoryQuantity,
              // featured_media: node.featuredMedia ? { id: parseInt(node.featuredMedia.id.replace(/[^0-9]/g, ''), 10) } : null,
              featured_media: node.image
              ? (() => {
                  const imgId = parseInt(node.image.id.replace(/[^0-9]/g, ''), 10);
                  const mediaId = imageToMediaMap[imgId] || imgId;
                  return {
                    id: mediaId,
                    preview_image: { src: node.image.url ?? null }
                  };
                })()
              : null,
              unit_price_measurement: node.unitPriceMeasurement
                ? {
                    reference_value: node.unitPriceMeasurement.referenceValue,
                    reference_unit: node.unitPriceMeasurement.referenceUnit
                  }
                : null
            });

             if (!this.data.formatted[legacyId]) {
                this.data.formatted[legacyId] = {
                  price: formatMoney(price, theme.moneyFormat)
                };
                if (compareAtPrice && compareAtPrice > price) {
                  this.data.formatted[legacyId].compareAtPrice = formatMoney(
                    compareAtPrice,
                    theme.moneyFormat
                  );
                }
                // Add unitPrice if available
                if (node.unitPrice) {
                  const unitPriceCents = Math.round(parseFloat(node.unitPrice.amount) * 100);
                  this.data.formatted[legacyId].unitPrice = formatMoney(unitPriceCents, theme.moneyFormat);
                }
                if (node.weight) {
                  this.data.formatted[legacyId].weight = `${node.weight} ${node.weightUnit}`;
                }
              }
          });


          hasNextPage = variantData.pageInfo.hasNextPage;
          cursor = variantData.pageInfo.endCursor;
        }

        // Replace the Liquid-truncated array with the full set
        this.data.product.variants = allVariants;

        // Re-derive the selected variant now that we have full data

        this.variant = this.getSelectedVariant();

        // Re-run all UI updates that depend on variant data
        this.updateAvailability();
        this.updateAddToCartButton();
        this.updatePrice();
      } catch (err) {
        console.error('[variant-picker] Failed to fetch all variants:', err);
      }
    }

    

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  function formatMoney(cents, format) {
    if (!format) format = '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(cents / 100)).replace('.', ','));
  }

  customElements.define('variant-picker', VariantPicker);
}
